/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { DemoOrderValidationService, DemoOrderValidation_ValidationInfoDto, DemoOrderValidation_ValidationStateEnum } from "app/services/demo-order-validation.service";

/**
 * Page Component used in the Demo Order Validation Process. 
 * 
 * This Page is used to allow the User to enter the "Order Name" for the Order
 * being validated.  This calls the backend Service to lookup the information 
 * for that Order, make sure it exists and prepare itself to begin validating
 * that Order.  After this stage, the User should be forwarded to the Order
 * Dashboard allowing them to control the Reader and view the on-going 
 * Validation Results. 
 */
@Component({
  templateUrl: './demo-order-validation.enter-info.html',
  styleUrls: ['./demo-order-validation.enter-info.css']
})
export class DemoOrderValidationEnterInfoComponent implements OnInit, AfterViewInit {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

/** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Portal Service selected by User which will be performing the validation */
  portal: ServiceInstance;

  /** Order Name input value being modeled by the UI */
  orderName: string = '';

  /** Flag which inform some UI controls as to whether they should be enabled or not */
  isUiDisabled = false;

  /** Constructor */
  constructor(
    private demoOrderValidationService: DemoOrderValidationService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    // Get the data that was resolved in the background during the page transition
    this.route.data.subscribe(
      (data: {
        portal: ServiceInstance
      }) => {
        console.log(`Obtained Portal: '${data?.portal?.id}/${data?.portal?.name}'`);
        this.portal = data?.portal;
      })
  }

  /**
   * Initializes the component view.  
   * Called automatically by the Angular Component Lifecycle.
   * 
   * Used to auto-focus on the "Order Name" input field.  
   */
  ngAfterViewInit(): void {
    // Auto-focus on the "Order Name" input (Not using the "[autofocus]" as that only works the 1st time the page is displayed, not after migrating back after clearing / processing an order)
    const element = document.getElementById("dopv-orderName");
    if (element != null && element != undefined) {
      element.focus();
    }
  }

  /**
   * Called when the "Get Info" Button is clicked in the UI.
   * Calls the Server to get the "Order Information" for the information
   * entered in the UI. 
   */
  onGetInfoClicked(): void {
    if (!this.isUiDisabled) {
      this.isUiDisabled = true;
      this.indefiniteProgressDialog.show("Processing", "Obtaining Order info...");
      this.getInfo();
    }
  }

  /**
   * Calls the Service to pass in the Order Name entered in the UI. 
   * The Service then looks up the Order and preps it for Portal Validation if possible 
   * and returns the current state. 
   */
  async getInfo(): Promise<void> {
    let state: DemoOrderValidation_ValidationInfoDto = null;
    try {
      state = await this.demoOrderValidationService.enterOrderInfo(this.portal.id, this.orderName);
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isUiDisabled = false;
      this.handleError(error);
      return;
    }

    this.isUiDisabled = false;
    this.indefiniteProgressDialog.hide();
    console.log(`Obtained State after Enter Info: ${JSON.stringify(state)}`);

    if (state?.state == DemoOrderValidation_ValidationStateEnum.OrderBeingDisplayed) {
      this.router.navigate(['order', 'portal-validation', this.portal.id, 'dashboard'], {});
    }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}